import { Button, Card, Image, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { DeviceCapturePicRecVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CANOPY_STATE } from "@/meta/logistics/shipInfo";
import { RECOGNIZE_RESULT, RECORD_ADD_TO_MODEL_STATUS } from "@/meta/logistics/monitor";

export interface IdentifyRecordDetailPageProps {
    recordId?: number | string;
    loadDetail?: (id: number | string) => Promise<DeviceCapturePicRecVo>;
}
export async function loadIdentifyRecordDetail(id: number | string) {
    const response = await shippingRequestHttp.service.get<DeviceCapturePicRecVo>(`/device/capture/pic-rec/${id}`);
    return response.data;
}
const text = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;
/** 对齐旧 Vue 识别结果，在装卸中的记录详情展示预计剩余时长。 */
const recognizeResultText = (value?: number, remainingLayTime?: number) => {
    const result = enumText(RECOGNIZE_RESULT, value);
    const isHandling = value === RECOGNIZE_RESULT.LOADING.code || value === RECOGNIZE_RESULT.UNLOADING.code;
    return isHandling && remainingLayTime != null ? `${result}（预计剩余${remainingLayTime}h）` : result;
};
/** 识别记录详情在对象切换时清理旧数据和旧图片，避免异步响应污染当前记录。 */
export function IdentifyRecordDetailPage({
    recordId,
    loadDetail = loadIdentifyRecordDetail
}: IdentifyRecordDetailPageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const id = recordId ?? params.id;
    const [detail, setDetail] = useState<DeviceCapturePicRecVo>();
    const [error, setError] = useState("");
    useEffect(() => {
        let disposed = false;
        setDetail(undefined);
        setError("");
        if (!id) return;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setError("识别记录详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);
    if (!detail)
        return (
            <section aria-label="identify-record-detail-page">
                <Card>{error ? <Alert role="alert" message={error} type="error" /> : "加载中..."}</Card>
            </section>
        );
    return (
        <section aria-label="identify-record-detail-page">
            <Card>
                <ZhDetailHeader title="识别记录详情" />
                <ZhGrid cols={2} gap={[30, 20]}>
                    <ZhBaseItem label="设备名称" value={text(detail.device?.name)} />
                    <ZhBaseItem
                        label="识别结果"
                        value={recognizeResultText(detail.picRec?.recognizeResult, detail.picRec?.remainingLayTime)}
                    />
                    <ZhBaseItem label="棚架状态" value={enumText(CANOPY_STATE, detail.picRec?.canopyStateResult)} />
                    <ZhBaseItem label="状态" value={enumText(RECORD_ADD_TO_MODEL_STATUS, detail.picRec?.status)} />
                    <ZhBaseItem label="创建时间" value={text(detail.picRec?.createdTime?.replace("T", " "))} />
                </ZhGrid>
                <Space className="mt-4" size="large">
                    <div>
                        <p>识别图片</p>
                        {detail.picFile?.path ? (
                            <Image alt="识别图片" aria-label="识别图片" src={detail.picFile.path} />
                        ) : (
                            <span>{text(detail.picFile?.name)}</span>
                        )}
                    </div>
                    <div>
                        <p>匹配图片</p>
                        {detail.retrievalPicFile?.path ? (
                            <Image alt="匹配图片" aria-label="匹配图片" src={detail.retrievalPicFile.path} />
                        ) : (
                            <span>{text(detail.retrievalPicFile?.name)}</span>
                        )}
                    </div>
                </Space>
                <Button className="mt-4" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Card>
        </section>
    );
}
